import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNotification } from "../../context/NotificationContext";
import universityService from "../../services/universityService";
import AppButton from "../../components/ui/AppButton";
import AppTable from "../../components/ui/AppTable";
import ListToolbar from "../../components/ui/ListToolbar";

const UniversityList = ({ onEdit, onView, onDelete, refreshTrigger }) => {
  const { t } = useTranslation(["universities", "common"]);
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 10,
    isLastPage: false,
  });
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);

  const { showSuccess } = useNotification();

  useEffect(() => {
    fetchUniversities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, refreshTrigger]);

  useEffect(() => {
    // Debounced search
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      if (search !== undefined) {
        fetchUniversities(0); // Reset to first page when searching
      }
    }, 500);

    setSearchTimeout(timeout);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const fetchUniversities = async (page = pagination.currentPage) => {
    try {
      setLoading(true);
      const response = await universityService.getUniversities(
        page,
        pagination.pageSize,
        search
      );

      if (response.success) {
        setUniversities(response.data.data);
        setPagination({
          currentPage: response.data.currentPage,
          totalPages: response.data.totalPages,
          totalElements: response.data.totalElements,
          pageSize: response.data.pageSize,
          isLastPage: response.data.isLastPage,
        });
      }
    } catch (error) {
      console.error("Failed to fetch universities:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  };

  const handlePageSizeChange = (newSize) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: parseInt(newSize),
      currentPage: 0,
    }));
  };

  const handleDelete = async (university) => {
    if (
      window.confirm(t("universities:universities.messages.confirmDelete"))
    ) {
      try {
        const response = await universityService.deleteUniversity(
          university.id
        );
        if (response.success) {
          showSuccess(t("universities:universities.messages.deleteSuccess"));
          fetchUniversities();
        }
      } catch (error) {
        console.error("Failed to delete university:", error);
      }
    }
  };

  const handleToggleStatus = async (university) => {
    const newStatus = !university.status;
    const actionKey = newStatus ? "buttons.activate" : "buttons.deactivate";

    if (
      window.confirm(
        `${t(`common:${actionKey}`)} "${university.nameEn}"?`
      )
    ) {
      try {
        const updateData = {
          nameEn: university.nameEn,
          nameLocal: university.nameLocal,
          mailPattern: university.mailPattern,
          status: newStatus,
        };

        const response = await universityService.updateUniversity(
          university.id,
          updateData
        );
        if (response.success) {
          showSuccess(newStatus ? t("universities:universities.messages.updateSuccess") : t("universities:universities.messages.updateSuccess"));
          fetchUniversities();
        }
      } catch (error) {
        console.error("Failed to toggle status:", error);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="university-list">
      <ListToolbar
        title={t("universities:universities.title")}
        subtitle={t("universities:universities.list.found", { count: pagination.totalElements })}
      >
        <div className="row g-3">
          <div className="col-md-6">
            <div className="input-group">
              <span className="input-group-text bg-light border-end-0">
                <i className="fas fa-search text-muted"></i>
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder={t("universities:filters.searchByName")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-6 d-flex align-items-center justify-content-end gap-2">
            <span className="text-muted small">{t("common:pagination.showing")}:</span>
            <select
              className="form-select form-select-sm"
              style={{ width: "auto" }}
              value={pagination.pageSize}
              onChange={(e) => handlePageSizeChange(e.target.value)}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-muted small">{t("common:pagination.entries")}</span>
          </div>
        </div>
      </ListToolbar>

      {/* Universities Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t("common:actions.loading")}</span>
              </div>
              <p className="text-muted mt-2">{t("universities:universities.list.loading")}</p>
            </div>
          ) : universities.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="fas fa-university text-muted mb-3"
                style={{ fontSize: "3rem" }}
              ></i>
              <h5 className="text-muted">{t("universities:universities.list.noFound")}</h5>
              <p className="text-muted">
                {search
                  ? t("universities:universities.list.noMatch", { search })
                  : t("universities:universities.list.noAvailable")}
              </p>
            </div>
          ) : (
            <AppTable responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t("universities:universities.table.university")}</th>
                    <th>{t("universities:universities.table.mailPattern")}</th>
                    <th>{t("universities:universities.table.status")}</th>
                    <th>{t("universities:universities.table.created")}</th>
                    <th className="text-center">{t("common:table.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {universities.map((university, index) => (
                    <tr key={university.id}>
                      <td className="align-middle">
                        {pagination.currentPage * pagination.pageSize +
                          index +
                          1}
                      </td>
                      <td className="align-middle">
                        <div className="d-flex align-items-center">
                          <div
                            className={`university-icon rounded-circle d-flex align-items-center justify-content-center me-3 ${
                              university.status ? "bg-primary" : "bg-secondary"
                            }`}
                            style={{ width: "40px", height: "40px" }}
                          >
                            <i className="fas fa-university text-white"></i>
                          </div>
                          <div>
                            <div className="fw-bold text-dark">
                              {university.nameEn}
                            </div>
                            <small className="text-muted">
                              {university.nameLocal}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td className="align-middle">
                        <span className="badge bg-light text-dark border">
                          <i className="fas fa-envelope me-1"></i>
                          {university.mailPattern}
                        </span>
                      </td>
                      <td className="align-middle">
                        <span
                          className={`badge ${
                            university.status ? "bg-success" : "bg-danger"
                          }`}
                        >
                          <i
                            className={`fas ${
                              university.status
                                ? "fa-check-circle"
                                : "fa-times-circle"
                            } me-1`}
                          ></i>
                          {university.status ? t("common:status.active") : t("common:status.inactive")}
                        </span>
                      </td>
                      <td className="align-middle">
                        <small className="text-muted">
                          {formatDate(university.createdAt)}
                        </small>
                      </td>
                      <td className="align-middle text-center">
                        <div className="btn-group" role="group">
                          <AppButton
                            variant="outline"
                            className="btn-sm text-info border-info"
                            onClick={() => onView(university)}
                            title={t("common:buttons.viewDetails")}
                          >
                            <i className="fas fa-eye"></i>
                          </AppButton>
                          <AppButton
                            variant="outline"
                            className="btn-sm text-primary border-primary"
                            onClick={() => onEdit(university)}
                            title={t("universities:universities.edit")}
                          >
                            <i className="fas fa-edit"></i>
                          </AppButton>
                          <AppButton
                            variant="outline"
                            className={`btn-sm ${
                              university.status
                                ? "text-warning border-warning"
                                : "text-success border-success"
                            }`}
                            onClick={() => handleToggleStatus(university)}
                            title={
                              university.status ? t("common:buttons.deactivate") : t("common:buttons.activate")
                            }
                          >
                            <i
                              className={`fas ${
                                university.status ? "fa-pause" : "fa-play"
                              }`}
                            ></i>
                          </AppButton>
                          <AppButton
                            variant="outline"
                            className="btn-sm text-danger border-danger"
                            onClick={() => handleDelete(university)}
                            title={t("common:buttons.delete")}
                          >
                            <i className="fas fa-trash"></i>
                          </AppButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
            </AppTable>
          )}
        </div>

        {/* Pagination */}
        {!loading && universities.length > 0 && (
          <div className="card-footer bg-light border-0">
            <div className="row align-items-center">
              <div className="col-md-6">
                <span className="text-muted small">
                  {t("common:pagination.showingEntries", {
                    start: pagination.currentPage * pagination.pageSize + 1,
                    end: Math.min((pagination.currentPage + 1) * pagination.pageSize, pagination.totalElements),
                    total: pagination.totalElements,
                  })}
                </span>
              </div>
              <div className="col-md-6">
                <nav>
                  <ul className="pagination pagination-sm justify-content-end mb-0">
                    <li
                      className={`page-item ${
                        pagination.currentPage === 0 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          handlePageChange(pagination.currentPage - 1)
                        }
                        disabled={pagination.currentPage === 0}
                      >
                        Previous
                      </button>
                    </li>

                    {/* Page numbers */}
                    {Array.from(
                      { length: Math.min(5, pagination.totalPages) },
                      (_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i;
                        } else if (pagination.currentPage < 3) {
                          pageNum = i;
                        } else if (
                          pagination.currentPage >=
                          pagination.totalPages - 2
                        ) {
                          pageNum = pagination.totalPages - 5 + i;
                        } else {
                          pageNum = pagination.currentPage - 2 + i;
                        }

                        return (
                          <li
                            key={pageNum}
                            className={`page-item ${
                              pagination.currentPage === pageNum ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum + 1}
                            </button>
                          </li>
                        );
                      }
                    )}

                    <li
                      className={`page-item ${
                        pagination.isLastPage ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          handlePageChange(pagination.currentPage + 1)
                        }
                        disabled={pagination.isLastPage}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UniversityList;
